<template>
  <div
    v-if="showBanner"
    :role="state === 'failed' ? 'alert' : 'status'"
    aria-live="polite"
    class="bg-warning-bg border-b border-warning-border px-4 sm:px-6 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-2 text-warning-ink"
  >
    <!-- The account banner (the alerts redesign): readable amber under the top
         bar, a real button, and the resend result said in place (Sending… → New
         link sent for 6 s, or why it failed with Try again) instead of a toast. -->
    <Mail :size="18" class="shrink-0 text-warning" aria-hidden="true" />
    <p class="flex-1 min-w-[200px] text-sm leading-5 py-2">
      <template v-if="state === 'failed'"><span class="font-semibold">Couldn’t send the link.</span> {{ failure }}</template>
      <template v-else><span class="font-semibold">Verify your email to use every feature.</span> We sent a link to {{ authStore.user?.email }}.</template>
    </p>
    <span v-if="state === 'sent'" class="shrink-0 inline-flex items-center gap-1.5 px-2.5 text-[13px] font-semibold text-success-ink">
      <CircleCheck :size="16" class="text-success" aria-hidden="true" />
      New link sent
    </span>
    <button
      v-else
      type="button"
      :disabled="state === 'sending'"
      class="shrink-0 h-9 px-3.5 inline-flex items-center gap-2 rounded-[10px] border border-warning-border bg-surface text-[13px] font-semibold text-warning-ink hover:border-warning transition-colors disabled:opacity-85 disabled:cursor-default"
      @click="resendVerification"
    >
      <Loader2 v-if="state === 'sending'" :size="14" class="animate-spin motion-reduce:animate-none" aria-hidden="true" />
      {{ state === 'sending' ? 'Sending…' : state === 'failed' ? 'Try again' : 'Resend email' }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref } from 'vue'
import { CircleCheck, Loader2, Mail } from 'lucide-vue-next'
import { extractApiError } from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { authService } from '@/services/auth.service'

const authStore = useAuthStore()

/** How long "New link sent" stays before the banner goes back to its default. */
const SENT_MS = 6000

const state = ref<'idle' | 'sending' | 'sent' | 'failed'>('idle')
const failure = ref('')
let sentTimer: ReturnType<typeof setTimeout> | undefined

const showBanner = computed(() => authStore.isAuthenticated && authStore.user && !authStore.user.email_verified)

async function resendVerification() {
  if (!authStore.user?.email || state.value === 'sending') return
  state.value = 'sending'
  try {
    await authService.resendVerification({ email: authStore.user.email })
    state.value = 'sent'
    clearTimeout(sentTimer)
    sentTimer = setTimeout(() => { state.value = 'idle' }, SENT_MS)
  } catch (err) {
    failure.value = extractApiError(err, 'Wait a minute and try again.')
    state.value = 'failed'
  }
}

onBeforeUnmount(() => clearTimeout(sentTimer))
</script>
